import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Modal } from 'antd'
import _ from 'lodash'

import { ColourButton, Text } from 'portal/lib/primitives'
import { AlertRecipientSelector, RecipientType } from './AlertRecipientSelector'
import { ALERT_RULE_TITLE_MAPPER } from './constants'
import {
  AlertRuleContainer,
  Description,
  RecipientsWrapper,
  SettingArea,
  TextArea
} from './sharedStyledComponents'

export const AlertRecipients = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules
}) => {
  const [isEditRecipientsModalOpen, setIsEditRecipientsModalOpen] =
    useState(false)
  const [unsavedSelectedRecipients, setUnsavedSelectedRecipients] = useState<
    RecipientType[]
  >([])

  const projectUsers = useSelector((state: any) => state.projectUsers)

  useEffect(() => {
    if (alertRules[alertKey]?.recipients) {
      setUnsavedSelectedRecipients(alertRules[alertKey].recipients)
    }
  }, [alertRules, alertKey])

  const handleEditRecipients = () => {
    setIsEditRecipientsModalOpen(true)
  }

  const updateRecipients = () => {
    const updatedAlertRules = {
      ...alertRules,
      [alertKey]: {
        ...alertRules[alertKey],
        recipients: unsavedSelectedRecipients
      }
    }
    setAlertRules(updatedAlertRules)
    setIsEditRecipientsModalOpen(false)
  }

  const convertUserIdArrayToDisplayNameArray = (
    userIdArray: RecipientType[]
  ) => {
    if (projectUsers.loading) {
      return 'Loading...'
    }

    const displayNameArray = userIdArray.map(
      recepient =>
        projectUsers.users[`${projectId}_${recepient.userId}`]?.displayName
    )
    return displayNameArray.sort((a, b) => a.localeCompare(b)).join(', ')
  }

  type RuleWithRecipientsType = {
    alertKey: string
    recipients: RecipientType[]
  }

  const otherRulesWithRecipients = useMemo(() => {
    if (!alertRules) return []

    const tempArray: RuleWithRecipientsType[] = []
    Object.keys(alertRules).forEach(key => {
      if (key !== alertKey && alertRules[key]?.recipients?.length > 0) {
        tempArray.push({
          alertKey: key,
          recipients: alertRules[key].recipients
        })
      }
    })

    return tempArray
  }, [alertRules, alertKey])

  const handleCancel = () => {
    setUnsavedSelectedRecipients(alertRules[alertKey]?.recipients || [])
    setIsEditRecipientsModalOpen(false)
  }

  return (
    <>
      <AlertRuleContainer style={{ marginTop: '1rem', alignItems: 'baseline' }}>
        <TextArea>
          <Description>Email alert recipients</Description>
        </TextArea>
        <SettingArea>
          <RecipientsWrapper>
            {alertRules[alertKey].recipients?.length > 0 && (
              <div>
                {convertUserIdArrayToDisplayNameArray(
                  alertRules[alertKey].recipients || []
                )}
              </div>
            )}
            <ColourButton.Basic onClick={handleEditRecipients}>
              Edit recipients
            </ColourButton.Basic>
          </RecipientsWrapper>
        </SettingArea>
      </AlertRuleContainer>
      <Modal
        title={
          <Text dim>
            {ALERT_RULE_TITLE_MAPPER[alertKey].toUpperCase()} ALERT
          </Text>
        }
        open={isEditRecipientsModalOpen}
        onCancel={handleCancel}
        onOk={updateRecipients}
        destroyOnHidden={true}
        footer={[
          <ColourButton.Basic
            key="back"
            type="button"
            onClick={handleCancel}
            style={{ display: 'inline-block', marginRight: '.75rem' }}
          >
            Cancel
          </ColourButton.Basic>,
          <ColourButton.Action
            key="submit"
            type="button"
            onClick={updateRecipients}
            style={{ display: 'inline-block' }}
            disabled={_.isEqual(
              unsavedSelectedRecipients,
              alertRules[alertKey].recipients
            )}
          >
            OK
          </ColourButton.Action>
        ]}
        width={850}
      >
        <AlertRecipientSelector
          projectId={projectId}
          unsavedSelectedRecipients={unsavedSelectedRecipients}
          setUnsavedSelectedRecipients={setUnsavedSelectedRecipients}
          otherRulesWithRecipients={
            otherRulesWithRecipients.length > 0
              ? otherRulesWithRecipients
              : null
          }
        />
      </Modal>
    </>
  )
}
